<template>
  <div class="max-w-4xl mx-auto p-6">
    <CryptoAssetModal
      :key="assetType"
      :value="showAssetModal"
      :action-text="assetModalActionText"
      modal-type="crypto"
      data-to-use="wallets"
      @asset-clicked="handleAssetSelectFromModal"
      @input="showAssetModal = $event"
    />

    <v-dialog
      :value="showSwapQuoteModal"
      max-width="500"
      :persistent="loadingSwapQuote"
      @input="onSwapQuoteModalInput"
    >
      <v-card v-if="swapQuote && currentAsset && currentToAsset">
        <v-card-title class="d-flex align-center justify-space-between">
          {{ confirmTitle }}
          <v-btn
            icon
            small
            :disabled="loadingSwapQuote"
            aria-label="Close"
            @click="onSwapQuoteModalInput(false)"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="flex flex-row items-center justify-between mt-8">
            <div class="flex flex-col items-center justify-center">
              <div class="flex flex-row items-center gap-2 text-center mb-2">
                <img
                  class="w-6 h-6 rounded-full overflow-hidden"
                  :src="currentAsset.logo"
                  :alt="`${currentAsset.symbol} logo`"
                />
                <span>{{ currentAsset.symbol }}</span>
              </div>
              <span>{{ swapQuote.from_amount }} {{ currentAsset.symbol }}</span>
            </div>
            <div class="flex flex-col items-center justify-center">
              <v-icon>mdi-arrow-right</v-icon>
            </div>
            <div class="flex flex-col items-center justify-center">
              <div class="flex flex-row items-center gap-2 text-center mb-2">
                <img
                  class="w-6 h-6 rounded-full overflow-hidden"
                  :src="currentToAsset.logo"
                  :alt="`${currentToAsset.symbol} logo`"
                />
                <span>{{ currentToAsset.symbol }}</span>
              </div>
              <span>{{ swapQuote.to_amount }} {{ currentToAsset.symbol }}</span>
            </div>
          </div>
          <div class="mt-4">
            <div class="grid grid-cols-2 gap-4">
              <div>Rate</div>
              <div class="text-left">
                1 {{ swapQuote.from_currency }} = {{ swapQuote.rate }}
                {{ swapQuote.to_currency }}
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>From</div>
              <div class="text-left">
                {{ swapQuote.from_amount }} {{ swapQuote.from_currency }}
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>To</div>
              <div class="text-left">
                {{ swapQuote.to_amount }} {{ swapQuote.to_currency }}
              </div>
            </div>
          </div>

          <div
            class="mt-8 text-center"
            :class="{ 'error--text': quoteIsExpired }"
          >
            <span v-if="quoteIsExpired">
              This quote has expired. Refresh to get a new rate.
            </span>
            <span v-else>Quote expires in {{ swapQuoteExpiresIn }} seconds</span>
          </div>

          <div class="mt-8">
            <v-btn
              color="primary"
              block
              @click="confirmQuote"
              :loading="loadingSwapQuote"
              :disabled="loadingSwapQuote || quoteIsExpired"
            >
              {{ confirmActionLabel }}
            </v-btn>
            <v-btn
              color="primary"
              text
              block
              @click="refreshQuote"
              :loading="loadingSwapQuote"
              :disabled="loadingSwapQuote"
            >
              Refresh Quote
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center">
        <v-btn icon small class="mr-3" @click="goBack" aria-label="Go back">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-gray-800 font-medium">Back</span>
      </div>

      <h1 class="text-3xl font-bold text-gray-800 mb-2 mt-4">Swap Token</h1>
    </div>

    <div
      v-if="initialLoading"
      class="rounded-xl border border-gray-200 bg-white p-8 text-center"
    >
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div v-else-if="currentAsset && (isNgnxFromSelected || walletAddressData)">
      <div class="flex flex-row justify-between items-center">
        <span>From:</span>
        <span v-if="isNgnxFromSelected">
          Balance: ₦ {{ formattedFiatBalance }}
        </span>
        <span v-else>
          Balance: {{ formattedWalletBalance }} {{ selectedAssetSymbol }}
        </span>
      </div>
      <div class="flex flex-row items-center gap-2 justify-between mt-4">
        <div
          class="inline-flex flex-row items-center gap-2 bg-white rounded-lg p-2 border border-gray-200 cursor-pointer"
          @click="handleShowAssetModel('from')"
        >
          <div class="w-6 h-6 rounded-full overflow-hidden">
            <img
              class="w-full h-full object-cover"
              :src="currentAsset.logo"
              :alt="`${currentAsset.symbol} logo`"
            />
          </div>
          <span>{{ currentAsset.symbol }}</span>
          <span>
            <v-icon>mdi-chevron-down</v-icon>
          </span>
        </div>
        <div class="flex flex-row items-center gap-2 justify-end flex-1 min-w-0">
          <label class="sr-only" for="swap-amount-input">Amount to swap</label>
          <input
            id="swap-amount-input"
            type="number"
            :value="amount"
            @input="onFromInput($event.target.value)"
            min="0"
            step="any"
            class="w-1/2 max-w-xs bg-white rounded-lg p-2 border border-gray-200 text-right"
            :class="{ 'text-gray-500': lastEdited === 'to' && amount !== '' }"
            placeholder="0.00"
            aria-label="Amount to swap"
          />
        </div>
      </div>
      <div class="grid grid-cols-4 gap-4 mt-4">
        <button
          type="button"
          @click="handleAmount(25)"
          class="bg-gray-200 rounded-lg p-2"
        >
          25%
        </button>
        <button
          type="button"
          @click="handleAmount(50)"
          class="bg-gray-200 rounded-lg p-2"
        >
          50%
        </button>
        <button
          type="button"
          @click="handleAmount(75)"
          class="bg-gray-200 rounded-lg p-2"
        >
          75%
        </button>
        <button
          type="button"
          @click="handleAmount(100)"
          class="bg-gray-200 rounded-lg p-2"
        >
          100%
        </button>
      </div>
      <div class="my-8 flex flex-row items-center justify-center">
        <v-divider></v-divider>
        <v-icon
          class="mx-4 rotate-90 transform"
          role="button"
          tabindex="0"
          aria-label="Swap from and to assets"
          @click="handleSwapAssets"
          @keyup.enter="handleSwapAssets"
        >
          mdi-swap-horizontal
        </v-icon>
        <v-divider></v-divider>
      </div>
      <div class="flex flex-row justify-between items-center mt-4">
        <span>To:</span>
        <span v-if="isNgnxToSelected && currentToAsset"> Amount (₦) </span>
      </div>
      <div class="flex flex-row items-center gap-2 justify-between mt-4">
        <div
          class="inline-flex flex-row items-center gap-2 bg-white rounded-lg p-2 border border-gray-200 cursor-pointer"
          @click="handleShowAssetModel('to')"
        >
          <div
            v-if="currentToAsset"
            class="w-6 h-6 rounded-full overflow-hidden"
          >
            <img
              class="w-full h-full object-cover"
              :src="currentToAsset.logo"
              :alt="`${currentToAsset.symbol} logo`"
            />
          </div>
          <span>{{ selectedToAssetSymbol || 'Select Asset' }}</span>
          <span>
            <v-icon>mdi-chevron-down</v-icon>
          </span>
        </div>
        <div
          class="flex flex-row items-center gap-2 justify-end flex-1 min-w-0"
        >
          <label class="sr-only" for="swap-to-amount-input">
            Amount to receive
          </label>
          <input
            id="swap-to-amount-input"
            type="number"
            :value="toAmount"
            @input="onToInput($event.target.value)"
            min="0"
            step="any"
            class="w-1/2 max-w-xs bg-white rounded-lg p-2 border border-gray-200 text-right"
            :class="{ 'text-gray-500': lastEdited === 'from' && toAmount !== '' }"
            placeholder="0.00"
            aria-label="Amount to receive"
          />
        </div>
      </div>

      <p v-if="rateError" class="mt-2 text-sm error--text">
        {{ rateError }}
      </p>
      <p
        v-else-if="currentAsset && currentToAsset"
        class="mt-2 text-sm text-gray-500"
      >
        {{
          pairRate
            ? 'Estimated - the exact figure is confirmed on the quote.'
            : 'Enter an amount, then get a quote for the exact figure.'
        }}
      </p>


      <p v-if="amountExceedsBalance" class="mt-2 text-sm error--text">
        Amount exceeds your available balance.
      </p>

      <p
        v-if="tradeMode === 'swap' && !swapIsEnabled"
        class="mt-2 text-sm error--text"
      >
        Swaps are currently disabled for {{ selectedAssetSymbol }}.
      </p>

      <p
        v-if="tradeMode === 'sell' && !saleIsEnabled"
        class="mt-2 text-sm error--text"
      >
        Sales are currently disabled for {{ selectedAssetSymbol }}.
      </p>

      <div class="mt-8">
        <v-btn
          @click="getQuote"
          :disabled="!canGetQuote || loadingSwapQuote"
          :loading="loadingSwapQuote"
          block
          large
          color="primary"
        >
          {{ ctaLabel }}
        </v-btn>
      </div>
    </div>

    <div
      v-else
      class="rounded-xl border border-gray-200 bg-white p-8 text-center"
    >
      <p class="text-gray-700 mb-4">
        We could not load your wallet for this asset. Try another coin from
        crypto, or fund your wallet first.
      </p>
      <div class="d-flex flex-wrap justify-center gap-2">
        <v-btn color="primary" outlined @click="goBack">Go back</v-btn>
        <v-btn color="primary" to="/user/products/crypto">Crypto</v-btn>
        <v-btn color="primary" text to="/user/wallet">Wallet</v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import cryptocoin from '../../../../api/users/cryptocoin'
import CryptoAssetModal from '../../../../components/CryptoAssetModal.vue'
import { updateWallet } from '../../../../utils/helpers'
import { formatCryptoAmount } from '../../../../utils/cryptoHistory'

export default {
  components: {
    CryptoAssetModal
  },
  data() {
    return {
      initialLoading: true,
      showSwapQuoteModal: false,
      selectedAssetSymbol: '',
      showAssetModal: false,
      assetType: 'from',
      cryptoAssets: [],
      currentAsset: null,
      currentToAsset: null,
      selectedToAssetSymbol: '',
      coinId: '',
      walletAddressData: null,
      amount: '',
      toAmount: '',
      // Either row can be typed in; the other is derived from `pairRate` and
      // stays an estimate until the quote lands.
      lastEdited: 'from',
      pairRate: null,
      rateError: '',
      rateToken: 0,
      loadingSwapQuote: false,
      swapQuote: null,
      swapQuoteExpiresIn: 0,
      quoteCountdownTimer: null,
      // Mirrors CryptoAssetModal's hardcoded fiatAssets[0] so the "From"
      // block can pre-select Naira without waiting on that modal to open.
      ngnxAsset: {
        symbol: 'NGNX',
        name: 'Nigerian Naira',
        logo:
          'https://cdn3d.iconscout.com/3d/premium/thumb/nigerian-naira-3d-illustration-download-in-png-blend-fbx-gltf-file-formats--financial-money-currency-symbol-pack-business-illustrations-3865857.png'
      }
    }
  },
  computed: {
    ...mapState({
      fiatWallet: (state) => state.user.wallet
    }),
    assetModalActionText() {
      // Naira may sit on either side - as the source it buys crypto, as the
      // target it sells crypto. It just can't be both, so the opposite side
      // only offers the Naira tab while this side holds a crypto.
      const canPickNaira =
        this.assetType === 'from' ? !this.isNgnxToSelected : !this.isNgnxFromSelected
      return canPickNaira
        ? { t1: 'Crypto', t2: 'Naira' }
        : { t1: 'Select Asset', t2: 'Select Asset' }
    },
    isNgnxFromSelected() {
      return !!this.currentAsset && this.currentAsset.isFiat === true
    },
    isNgnxToSelected() {
      return !!this.currentToAsset && this.currentToAsset.isFiat === true
    },
    // Which endpoint family settles this trade. Naira as the source is a
    // purchase, naira as the target is a sale, anything else is a swap.
    tradeMode() {
      if (this.isNgnxFromSelected) return 'buy'
      if (this.isNgnxToSelected) return 'sell'
      return 'swap'
    },
    formattedFiatBalance() {
      const n = Number(this.fiatWallet && this.fiatWallet.balance)
      if (!Number.isFinite(n)) return '0.00'
      return n.toLocaleString('en-NG', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    fromBalance() {
      const raw = this.isNgnxFromSelected
        ? this.fiatWallet && this.fiatWallet.balance
        : this.walletAddressData && this.walletAddressData.balance
      const n = Number(raw)
      return Number.isFinite(n) ? n : 0
    },
    confirmTitle() {
      if (this.tradeMode === 'buy') return 'Confirm Purchase'
      if (this.tradeMode === 'sell') return 'Confirm Sale'
      return 'Confirm Transaction'
    },
    confirmActionLabel() {
      if (this.tradeMode === 'buy') return 'Complete Purchase'
      if (this.tradeMode === 'sell') return 'Complete Sale'
      return 'Complete Swap'
    },
    ctaLabel() {
      if (this.tradeMode === 'buy') {
        return this.currentToAsset
          ? `Buy ${this.currentToAsset.symbol}`
          : 'Buy crypto'
      }
      if (this.tradeMode === 'sell') {
        return this.currentAsset
          ? `Sell ${this.currentAsset.symbol}`
          : 'Sell crypto'
      }
      return 'Get Swap Quote'
    },
    formattedWalletBalance() {
      if (!this.walletAddressData) return '0'
      return formatCryptoAmount(this.walletAddressData.balance)
    },
    numericAmount() {
      const n = Number(this.amount)
      return Number.isFinite(n) ? n : 0
    },
    numericBalance() {
      return this.fromBalance
    },
    amountExceedsBalance() {
      return this.numericAmount > this.fromBalance
    },
    swapFromCoinId() {
      return this.assetCoinId(this.currentAsset)
    },
    swapToCoinId() {
      return this.assetCoinId(this.currentToAsset)
    },
    swapIsEnabled() {
      return (
        !this.walletAddressData ||
        !this.walletAddressData.coin ||
        this.walletAddressData.coin.swapEnabled !== false
      )
    },
    saleIsEnabled() {
      return (
        !this.walletAddressData ||
        !this.walletAddressData.coin ||
        this.walletAddressData.coin.saleEnabled !== false
      )
    },
    canGetQuote() {
      if (!this.currentAsset || !this.currentToAsset) return false

      if (this.numericAmount <= 0 || this.amountExceedsBalance) return false
      if (this.tradeMode === 'buy') {
        return !!this.swapToCoinId
      }
      if (this.tradeMode === 'sell') {
        return !!this.swapFromCoinId && this.saleIsEnabled
      }
      return (
        !!this.swapFromCoinId &&
        !!this.swapToCoinId &&
        this.swapFromCoinId !== this.swapToCoinId &&
        this.swapIsEnabled
      )
    },
    quoteIsExpired() {
      return (
        this.showSwapQuoteModal &&
        !!this.swapQuote &&
        this.swapQuoteExpiresIn <= 0
      )
    }
  },
  beforeDestroy() {
    this.clearQuoteCountdown()
  },
  async mounted() {
    this.coinId = this.$route.params.id
    updateWallet()

    // Entry point from the dashboard's "Buy with Naira" tab - there's no
    // specific crypto to preselect, so skip the coinId-based lookup below
    // (it would fail since 'ngnx' matches no real coinId) and pre-select
    // Naira as "From" directly. The user then picks the target crypto
    // themselves via the "To" selector.
    try {
      if (this.$route.query.from === 'ngnx') {
        this.selectFiatFromAsset(this.ngnxAsset)
      } else {
        // Both fetches must land before we can decide whether to show the
        // swap form or the "could not load wallet" fallback - awaiting them
        // is what stops that fallback flashing on screen while they're
        // still in flight.
        await Promise.all([this.getCryptoAssets(), this.getCryptoAddress()])
      }
      await this.applyToQuery()
    } finally {
      this.initialLoading = false
    }
  },
  watch: {
    // Any change to the pair - or to the balance the probe is sized from -
    // invalidates the rate.
    currentAsset: 'fetchPairRate',
    currentToAsset: 'fetchPairRate',
    fromBalance: 'fetchPairRate'
  },
  methods: {
    assetCoinId(asset) {
      if (!asset) return ''
      return asset.coinId || (asset.coin && asset.coin._id) || ''
    },
    normalizeAsset(asset) {
      if (!asset) return null
      const coinId = asset.coinId || (asset.coin && asset.coin._id)
      return { ...asset, coinId }
    },
    alertUser(message, error = true) {
      this.$store.dispatch('alert', {
        message,
        status: true,
        error
      })
    },
    swapApiErrorMessage(res, fallback) {
      if (!res || !res.error) return fallback
      const em = res.errorMessage
      if (typeof em === 'string' && em.trim()) return em
      if (em && typeof em === 'object' && typeof em.message === 'string') {
        return em.message
      }
      return fallback
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push('/user/products/crypto')
      }
    },
    clearQuoteCountdown() {
      if (this.quoteCountdownTimer != null) {
        clearInterval(this.quoteCountdownTimer)
        this.quoteCountdownTimer = null
      }
    },
    startQuoteCountdown(expiresAt) {
      this.clearQuoteCountdown()
      const end = new Date(expiresAt).getTime()
      if (Number.isNaN(end)) {
        this.swapQuoteExpiresIn = 0
        return
      }
      const tick = () => {
        const sec = Math.max(0, Math.floor((end - Date.now()) / 1000))
        this.swapQuoteExpiresIn = sec
        if (sec <= 0) {
          this.clearQuoteCountdown()
        }
      }
      tick()
      this.quoteCountdownTimer = setInterval(tick, 1000)
    },
    onSwapQuoteModalInput(value) {
      this.showSwapQuoteModal = value
      if (!value) {
        this.clearQuoteCountdown()
      }
    },
    async getCryptoAssets() {
      try {
        const res = await cryptocoin.cryptoCoin().getCryptosRate()
        const currentCoin = res.data.data.find(
          (asset) => asset.coinId === this.coinId
        )

        if (currentCoin) {
          this.cryptoAssets = [
            {
              symbol: currentCoin.symbol,
              name: currentCoin.currency,
              logo: currentCoin.logo,
              price: currentCoin.price,
              percentageChange: currentCoin.percentageChange,
              coinId: currentCoin.coinId
            }
          ]
          this.selectAsset(this.cryptoAssets[0])
        } else {
          this.alertUser('This cryptocurrency is not available for swap.')
        }
      } catch (error) {
        this.alertUser(
          'Could not load cryptocurrency list. Please try again later.'
        )
      }
    },
    selectAsset(asset) {
      const normalized = this.normalizeAsset(asset)
      this.currentAsset = normalized
      this.selectedAssetSymbol = normalized.symbol
      this.showAssetModal = false
    },
    async getCryptoAddress() {
      const res = await cryptocoin.cryptoCoin().getCryptosWallets()
      if (res.error) {
        this.alertUser(res.message || 'Could not load wallets')
        return
      }

      const wallet = res.data.data.find((w) => w.coin._id === this.coinId)
      this.walletAddressData = wallet
      if (wallet?._id) {
        const fresh = await cryptocoin.cryptoCoin().getCryptoWallet(wallet._id)
        if (!fresh.error && fresh.data?.data) {
          this.walletAddressData = fresh.data.data
        }
      }
    },
    selectFiatFromAsset(asset) {
      this.currentAsset = { ...asset, isFiat: true }
      this.selectedAssetSymbol = asset.symbol
      this.showAssetModal = false
      this.walletAddressData = null
      this.resetAmounts()
    },
    // The pair changed, so both figures - and the estimate behind them - are
    // stale.
    resetAmounts() {
      this.amount = ''
      this.toAmount = ''
      this.lastEdited = 'from'
      this.swapQuote = null
      this.clearQuoteCountdown()
    },
    handleAssetSelectFromModal(asset, type) {
      if (this.assetType === 'from' && type === 'fiat') {
        this.selectFiatFromAsset(asset)
        return
      }

      const normalized = this.normalizeAsset(asset)
      if (this.assetType === 'from') {
        this.selectAsset(normalized)
        this.coinId = normalized.coinId
        this.resetAmounts()
        this.getCryptoAddress()
      }
      if (this.assetType === 'to') {
        // Naira as the target is a sale; it has no coin record, so flag it
        // rather than running it through normalizeAsset.
        this.currentToAsset =
          type === 'fiat' ? { ...asset, isFiat: true } : normalized
        this.selectedToAssetSymbol = asset.symbol
        this.resetAmounts()
      }
    },
    handleShowAssetModel(type) {
      this.showAssetModal = true
      this.assetType = type
    },
    handleSwapAssets() {
      if (!this.currentAsset || !this.currentToAsset) return

      // Flipping turns a purchase into a sale and back, so the naira flag has
      // to travel with the asset rather than being re-derived.
      const from = this.currentAsset.isFiat
        ? this.currentAsset
        : this.normalizeAsset(this.currentAsset)
      const to = this.currentToAsset.isFiat
        ? this.currentToAsset
        : this.normalizeAsset(this.currentToAsset)

      this.currentAsset = to
      this.currentToAsset = from
      this.selectedAssetSymbol = to.symbol
      this.selectedToAssetSymbol = from.symbol
      this.coinId = this.assetCoinId(to)
      // Both figures belong to the old direction, so start clean.
      this.resetAmounts()
      if (this.coinId) {
        this.getCryptoAddress()
      } else {
        this.walletAddressData = null
      }
    },
    handleAmount(percent) {
      if (!(this.fromBalance > 0)) return
      // 13.14484 * 100 / 100 is 13.144839999999999 in binary floating point,
      // and rounding that up puts the amount over the balance. 100% takes the
      // balance as it stands; the rest are trimmed to 8dp.
      const amount =
        percent === 100
          ? this.fromBalance
          : Number(((this.fromBalance * percent) / 100).toFixed(8))
      this.onFromInput(String(amount))
    },
    formatEstimate(value, isFiat) {
      if (!Number.isFinite(value) || value <= 0) return ''
      return isFiat ? value.toFixed(2) : String(Number(value.toPrecision(8)))
    },
    onFromInput(value) {
      this.lastEdited = 'from'
      this.amount = value
      this.toAmount = this.pairRate
        ? this.formatEstimate(Number(value) * this.pairRate, this.isNgnxToSelected)
        : ''
    },
    // Rounding the derived amount can land it a hair above the balance, which
    // the trade endpoints reject outright, so it never exceeds what is there.
    deriveFromAmount(toValue) {
      if (!this.pairRate) return ''
      const derived = Number(toValue) / this.pairRate
      if (!Number.isFinite(derived) || derived <= 0) return ''
      return derived >= this.fromBalance
        ? String(this.fromBalance)
        : this.formatEstimate(derived, this.isNgnxFromSelected)
    },
    onToInput(value) {
      this.lastEdited = 'to'
      this.toAmount = value
      this.amount = this.deriveFromAmount(value)
    },
    syncEstimate() {
      if (!this.pairRate) return
      if (this.lastEdited === 'from') {
        if (Number(this.amount) > 0) this.onFromInput(this.amount)
      } else if (Number(this.toAmount) > 0) {
        this.onToInput(this.toAmount)
      }
    },
    // One quote per pair gives the effective rate with the spread/markup
    // already in it, and every mode prices linearly, so both rows derive from
    // it. It is quoted at the user's own balance because the endpoints reject
    // an amount the wallet cannot cover.
    async fetchPairRate() {
      const token = ++this.rateToken
      this.pairRate = null
      this.rateError = ''
      if (!this.currentAsset || !this.currentToAsset) return
      if (!this.swapToCoinId && !this.isNgnxToSelected) return
      if (!(this.fromBalance > 0)) return

      try {
        const res = await this.tradeApi({}, this.fromBalance).get()
        if (token !== this.rateToken) return
        if (res.error) {
          // The estimate itself is best-effort, but a backend refusal
          // (unverified account, disabled pair) is the user's to see.
          this.rateError = this.swapApiErrorMessage(res, '')
          return
        }
        const quote = res.data && res.data.data
        if (!quote) return
        const rate = Number(quote.to_amount) / Number(quote.from_amount)
        if (Number.isFinite(rate) && rate > 0) {
          this.pairRate = rate
          this.syncEstimate()
        }
      } catch (e) {
        // Estimating is best-effort - the binding figures still come from the
        // quote taken when the user asks for one.
      }
    },
    // Preselects the "To" side from `?to=` so Buy and Sell can land on a
    // ready-made pair.
    async applyToQuery() {
      const to = this.$route.query.to
      if (!to) return
      if (to === 'ngnx') {
        this.currentToAsset = { ...this.ngnxAsset, isFiat: true }
        this.selectedToAssetSymbol = this.ngnxAsset.symbol
        return
      }
      const res = await cryptocoin.cryptoCoin().getCryptosRate()
      if (res.error) return
      const coin = (res.data.data || []).find((asset) => asset.coinId === to)
      if (!coin) return
      this.currentToAsset = {
        symbol: coin.symbol,
        name: coin.currency,
        logo: coin.logo,
        coinId: coin.coinId
      }
      this.selectedToAssetSymbol = coin.symbol
    },
    // Nothing moves until the confirm step runs, so every mode quotes first
    // and opens the confirm modal rather than reporting success here.
    //
    //   buy   NGNX -> crypto   amount in naira,  coinId = the coin bought
    //   sell  crypto -> NGNX   amount in crypto, coinId = the coin sold
    //   swap  crypto -> crypto amount in crypto, both coins named
    tradeApi(extra = {}, amount = this.amount) {
      const api = cryptocoin.cryptoCoin()

      if (this.tradeMode === 'buy') {
        const payload = {
          amount: Number(amount),
          coinId: this.swapToCoinId,
          ...extra
        }
        return {
          noun: 'purchase',
          get: () => api.getPurchaseRate({ ...payload, networkId: '' }),
          refresh: () => api.refreshPurchaseRate(payload),
          complete: () => api.completePurchaseRate(payload)
        }
      }

      if (this.tradeMode === 'sell') {
        const payload = {
          amount: Number(amount),
          coinId: this.swapFromCoinId,
          ...extra
        }
        return {
          noun: 'sale',
          get: () => api.getSaleRate(payload),
          refresh: () => api.refreshSaleRate(payload),
          complete: () => api.completeSaleRate(payload)
        }
      }

      const payload = {
        amount: Number(amount),
        from: this.swapFromCoinId,
        to: this.swapToCoinId,
        ...extra
      }
      return {
        noun: 'swap',
        get: () => api.getSwapRate(payload),
        refresh: () => api.refreshSwapRate(payload),
        complete: () => api.completeSwapRate(payload)
      }
    },
    tradeIsValid() {
      if (!this.currentAsset || !this.currentToAsset) {
        this.alertUser('Please select both assets')
        return false
      }

      if (this.numericAmount <= 0) {
        this.alertUser(
          this.tradeMode === 'buy'
            ? 'Enter how much Naira you want to spend'
            : 'Please enter an amount greater than zero'
        )
        return false
      }
      if (this.amountExceedsBalance) {
        this.alertUser('Amount exceeds your available balance')
        return false
      }
      if (this.tradeMode === 'buy') return true
      if (this.tradeMode === 'sell') {
        if (!this.saleIsEnabled) {
          this.alertUser(
            `Sales are currently disabled for ${this.selectedAssetSymbol}`
          )
          return false
        }
        return true
      }
      if (this.swapFromCoinId === this.swapToCoinId) {
        this.alertUser('You cannot swap the same asset')
        return false
      }
      if (!this.swapIsEnabled) {
        this.alertUser(
          `Swaps are currently disabled for ${this.selectedAssetSymbol}`
        )
        return false
      }
      return true
    },
    async getQuote() {
      if (!this.tradeIsValid()) return

      const trade = this.tradeApi()
      this.loadingSwapQuote = true
      try {
        const res = await trade.get()
        if (res.error) {
          this.alertUser(
            this.swapApiErrorMessage(
              res,
              `An error occurred while getting the ${trade.noun} quote`
            )
          )
          return
        }

        const quote = res.data && res.data.data
        if (!quote || !quote.quoteId) {
          this.alertUser('Invalid response from server. Please try again.')
          return
        }

        this.setQuote(quote)
        this.showSwapQuoteModal = true
      } catch (e) {
        this.alertUser(
          this.thrownErrorMessage(
            e,
            `Could not get ${trade.noun} quote. Check your connection and try again.`
          )
        )
      } finally {
        this.loadingSwapQuote = false
      }
    },
    async refreshQuote() {
      if (!this.swapQuote || !this.swapQuote.quoteId) return

      const trade = this.tradeApi({ quoteId: this.swapQuote.quoteId })
      this.loadingSwapQuote = true
      try {
        const res = await trade.refresh()
        if (res.error) {
          this.alertUser(
            this.swapApiErrorMessage(
              res,
              `An error occurred while refreshing the ${trade.noun} quote`
            )
          )
          return
        }

        const quote = res.data && res.data.data
        if (!quote) {
          this.alertUser('Invalid response from server. Please try again.')
          return
        }

        this.setQuote(quote)
        this.alertUser('Quote refreshed successfully', false)
      } catch (e) {
        this.alertUser(
          this.thrownErrorMessage(
            e,
            'Could not refresh quote. Check your connection and try again.'
          )
        )
      } finally {
        this.loadingSwapQuote = false
      }
    },
    async confirmQuote() {
      if (this.quoteIsExpired || !this.swapQuote) return

      const trade = this.tradeApi({ quoteId: this.swapQuote.quoteId })
      this.loadingSwapQuote = true
      try {
        const res = await trade.complete()
        if (res.error) {
          this.alertUser(
            this.swapApiErrorMessage(
              res,
              `An error occurred while completing the ${trade.noun}`
            )
          )
          return
        }

        const result = res.data && res.data.data
        this.clearQuoteCountdown()
        this.showSwapQuoteModal = false
        this.swapQuote = null

        // Settlement is asynchronous on every one of these - the provider
        // confirms later - so never claim the balance has already moved.
        if (result && result.status === 'failed') {
          this.alertUser(result.statusMessage || `${trade.noun} failed. Please try again.`)
          return
        }
        this.alertUser(
          (result && result.message) ||
            'Submitted and is being processed. Your balance will update shortly.',
          false
        )

        this.resetAmounts()
        updateWallet()
        this.$router.push('/user/dashboard')
      } catch (e) {
        this.alertUser(
          this.thrownErrorMessage(
            e,
            `Could not complete ${trade.noun}. Check your connection and try again.`
          )
        )
      } finally {
        this.loadingSwapQuote = false
      }
    },
    // Every quote shape carries from_amount/to_amount/expires_at, so the rate
    // and the countdown are derived the same way in all three modes.
    setQuote(quote) {
      this.swapQuote = quote
      const fromAmt = Number(quote.from_amount)
      const toAmt = Number(quote.to_amount)
      const rate = Number.isFinite(fromAmt) && fromAmt !== 0 ? toAmt / fromAmt : 0
      this.swapQuote.rate =
        rate >= 1 ? rate.toFixed(2) : String(Number(rate.toPrecision(4)))
      this.startQuoteCountdown(quote.expires_at)
    },
    thrownErrorMessage(e, fallback) {
      return (
        (e &&
          e.response &&
          e.response.data &&
          (e.response.data.message || e.response.data.error)) ||
        (e && e.message) ||
        fallback
      )
    }
  }
}
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
